import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Can I run multiple parking locations from one account?",
      answer: "Yes — manage all sites within a single dashboard. Park Hive is built for scalability and allows you to control multiple parking locations with centralized reporting, staff management, and real-time monitoring across all your facilities."
    },
    {
      question: "Do I need new hardware?",
      answer: "Not necessary. The system runs with your mobile device and can integrate with existing gates or cameras if required. Park Hive works with the simplicity of a mobile app — no complicated setup or heavy hardware installation needed."
    },
    {
      question: "What about internet issues?",
      answer: "Offline functionality ensures operations never stop. Park Hive includes offline-resilient architecture that maintains functionality even if internet access fluctuates, with automatic data synchronization once connectivity returns."
    },
    {
      question: "How long does it take to set up?",
      answer: "Just a few minutes — create your Hive, configure pricing, and begin check-ins. Park Hive offers instant setup with no months of installation or complex configuration required."
    },
    {
      question: "What types of payment methods does Park Hive support?",
      answer: "Park Hive supports comprehensive digital payment processing including UPI, credit/debit cards, mobile wallets, and contactless payments. This eliminates cash handling and provides customers with convenient, fast payment options."
    },
    {
      question: "How does Park Hive improve parking operations efficiency?",
      answer: "Park Hive eliminates bottlenecks with lightning-fast entry/exit processing, automated billing calculation, real-time space monitoring, and digital payment processing. This reduces waiting times, prevents revenue loss, and provides complete operational transparency."
    },
    {
      question: "What kind of reporting and analytics does Park Hive provide?",
      answer: "Park Hive offers comprehensive real-time analytics including revenue tracking, space utilization reports, peak hour analysis, customer behavior insights, and staff performance monitoring. All data is exportable for further analysis and business planning."
    },
    {
      question: "Is Park Hive suitable for different types of parking facilities?",
      answer: "Absolutely! Park Hive is designed to handle various parking environments including shopping malls, corporate parks, hospitals, residential communities, tourist attractions, event venues, and airports. It adapts to different operational needs and traffic patterns."
    },
    {
      question: "How does team management work in Park Hive?",
      answer: "Park Hive provides comprehensive team access controls with role-based permissions, staff accountability tracking, shift management, and performance monitoring. You can manage multiple staff members across different locations with complete oversight."
    },
    {
      question: "What support is available for Park Hive users?",
      answer: "We provide comprehensive support including setup assistance, training for your team, ongoing technical support, and regular system updates. Our support team is available to help you maximize the benefits of your parking management system."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleWhatsAppDemo = () => {
    const phoneNumber = "919384376329";
    const message = "Hi! I'm interested in learning more about Park Hive parking management system. Can you please provide me with a demo?";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600">
            Get answers to common questions about Park Hive parking management system
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md border border-gray-200">
                <button
                  className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  onClick={() => toggleFAQ(index)}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {openIndex === index ? (
                      <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              Still have questions? We're here to help!
            </p>
            <button 
              onClick={handleWhatsAppDemo}
              className="bg-green-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200 inline-flex items-center"
            >
              <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
              </svg>
              Contact us on WhatsApp
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;